import { enumBrokeredOrderStatus } from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import { brokeredOrderCloseAtom } from '../_graphql/mutationBrokeredOrderClose';
import { brokeredOrderOpenAtom } from '../_graphql/mutationBrokeredOrderOpen';
import { brokeredOrderUpdateAtom } from '../_graphql/mutationBrokeredOrderUpdate';
import { brokeredOrderVoidAtom } from '../_graphql/mutationBrokeredOrderVoid';
import { brokeredOrderDetailQueryAtom } from '../_graphql/queryBrokeredOrderDetail';
import { brokeredOrderValidateQueryAtom } from '../_graphql/queryBrokeredOrderValidate';
import { BrokeredOrderDetailRecord } from '../types/brokeredOrder';
import { BrokeredOrderLineItemRecord } from '../types/brokeredOrderLineItem';
import { ZERO_MONEY } from '../utils/lineItemCalculations';

import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';

// 🟣🟣🟣 PARAMS
export const $brokeredOrderDetailPathParams = atomFromRoutePath(
  '/brokerage/orders/:id' as const,
);

export const $tab = atomFromQueryString.string<
  'overview' | 'orders' | 'materials'
>('tab');

// 🔘🔘🔘 PLAIN ATOMS
export const $isLinkDialogOpen = atom(false);
export const $linkDialogType = atom<'purchaseOrder' | 'salesOrder'>(
  'purchaseOrder',
);

// 🟢🟢🟢 COMPUTED DATA -> brokered order id from route
export const $brokeredOrderId = atom((get) =>
  get($brokeredOrderDetailPathParams)?.id
    ? String(get($brokeredOrderDetailPathParams)?.id)
    : null,
);

// 🟢🟢🟢 REMOTE DATA -> brokered order detail
export const $remoteBrokeredOrderDetail = brokeredOrderDetailQueryAtom(
  (get) => ({
    variables: { id: get($brokeredOrderId) ?? '' },
    skip: !get($brokeredOrderId),
  }),
);

// 🟢🟢🟢 REMOTE DATA -> brokered order validation
export const $brokeredOrderValidation = brokeredOrderValidateQueryAtom(
  (get) => {
    const id = get($brokeredOrderId);
    const order = get($remoteBrokeredOrderDetail);
    const isVoided = order?.data?.status === enumBrokeredOrderStatus.Voided;
    return {
      variables: { id: id ?? '' },
      skip: !id || isVoided,
    };
  },
);

// 🟢🟢🟢 COMPUTED DATA -> loading state
export const $isBrokeredOrderLoading = atom((get) => {
  const data = get($remoteBrokeredOrderDetail);
  return data?.isLoading ?? false;
});

// 🟢🟢🟢 COMPUTED DATA -> error state
export const $brokeredOrderError = atom((get) => {
  const data = get($remoteBrokeredOrderDetail);
  return data?.error ?? null;
});

// 🟠🟠🟠 ACTION -> update brokered order
export const $updateBrokeredOrder = brokeredOrderUpdateAtom({
  onSuccess: (get) => {
    const id = get($brokeredOrderId);
    if (id) {
      get($remoteBrokeredOrderDetail).refetch?.();
      get($brokeredOrderValidation).refetch?.();
    }
  },
});

// 🟠🟠🟠 ACTION -> void brokered order
export const $voidBrokeredOrder = brokeredOrderVoidAtom({
  onSuccess: (get) => {
    const id = get($brokeredOrderId);
    if (id) {
      get($remoteBrokeredOrderDetail).refetch?.();
    }
  },
});

// 🟠🟠🟠 ACTION -> open brokered order
export const $openBrokeredOrder = brokeredOrderOpenAtom({
  onSuccess: (get) => {
    const id = get($brokeredOrderId);
    if (id) {
      get($remoteBrokeredOrderDetail).refetch?.();
    }
  },
});

// 🟠🟠🟠 ACTION -> close brokered order
export const $closeBrokeredOrder = brokeredOrderCloseAtom({
  onSuccess: (get) => {
    const id = get($brokeredOrderId);
    if (id) {
      get($remoteBrokeredOrderDetail).refetch?.();
    }
  },
});

// 🟡🟡🟡 LINE ITEMS STATE - Draft Line Items for Brokered Order

const mapOrderLineItemsToLocal = (
  orderLineItems: BrokeredOrderDetailRecord['lineItems'],
): BrokeredOrderLineItemRecord[] =>
  orderLineItems.map((li) => ({
    id: li.id,
    itemId: li.itemId,
    item: li.item,
    quantity: li.quantity,
    unit: li.unit,
    purchaseUnitPrice: li.purchaseUnitPrice ?? ZERO_MONEY,
    salesUnitPrice: li.salesUnitPrice ?? ZERO_MONEY,
    purchaseTotal: li.purchaseTotal ?? ZERO_MONEY,
    salesTotal: li.salesTotal ?? ZERO_MONEY,
    margin: li.margin ?? ZERO_MONEY,
    marginPercent: li.marginPercent ?? 0,
    notes: li.notes,
  }));

// Draft line items for brokered order (null means use remote data)
export const $brokeredOrderDraftLineItems = atom<
  BrokeredOrderLineItemRecord[] | null
>(null);

// Effective line items (draft or remote)
export const $brokeredOrderEffectiveLineItems = atom((get) => {
  const draft = get($brokeredOrderDraftLineItems);
  if (draft !== null) {
    return draft;
  }
  const orderData = get($remoteBrokeredOrderDetail);
  return orderData?.data?.lineItems
    ? mapOrderLineItemsToLocal(orderData.data.lineItems)
    : [];
});

// Track if there are unsaved changes
export const $brokeredOrderHasLineItemChanges = atom((get) => {
  const draft = get($brokeredOrderDraftLineItems);
  return draft !== null;
});
